import * as React from "react";
import { Tooltip } from "react-tooltip";
import { Scope, useFormState, useFormApi } from "informedv4";
import { RequiredNotBlank } from "../../forms/validation/Required";
import { Dropdown } from "../../forms/informedv4/DropdownV4";
import { TextArea } from "../../forms/informedv4/TextAreaV4";
import Loading, { FontSize } from "../../misc/components/loading";
import { DcfRecordState, Correction, MiscCorrection, DcfRequestState, correctionTypeIdToWizard } from "./DcfRequestReducer";
import FileAttachmentsUploader from "../../file-attachments/components/uploader/FileAttachmentsUploader";
import { AttachmentRelatesTo } from "../../file-attachments/models";
import { CorrectionTypeSelection } from '../Execution/CorrectionTypeSelection';
import { CorrectionPreviewPanel } from '../Execution/CorrectionsPreviewPanel';
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { QueryOfVisit, Subject, WebPortalClient, QueryOfSession, Session, Visit, DcfRequest } from "webportalclient";
import { DataCorrectionRequestFormFields } from "./DataCorrectionRequestForm";
import { DcfRequestAction, LoadingSessionOptionsAction, LoadingVisitOptionsAction, LoadSessionOptionsAction, LoadVisitOptionsAction, SelectSessionOptionAction, SelectVisitOptionAction } from "./DcfRequestActions";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { GetSessionLabel } from "./Selectors/SiteOptions";
import { ConfigurableDataItemIds } from "../../misc/api/ApiConfigurableDataItems";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DcfCorrection } from "webportalclient";
import { CorrectionTypesEnum } from "../../misc/api/apiTypes";
import { useSecurityContext } from "../../Core/SecurityContext";

export interface DataCorrectionRequestFormRecordProps {
    recordId: string;
    setRecordFileAttachmentUploader: (
        recordIndex: number,
        upload: any
    ) => void;
    onFileAttachmentChanged: (fa: any[]) => void;
    recordIndex: number;
    subjectOptions: Subject[];
    visits: DcfRecordState<QueryOfVisit>;
    sessions: DcfRecordState<QueryOfSession>;
    isEditFormRecord?: boolean;
    isLoadingSubjectOptions: boolean;
    isRemoveRecordButtonDisabled: boolean;
    onRemoveClick: (id: string) => void;
    onSubjectChange: (subjectId: string) => void;
    onVisitChange: (visitId: string) => void;
    onSessionChange: (sessionId: string) => void;
    selectedSubject: Subject;
    selectedVisit: Visit;
    selectedSession: Session;
    corrections: Correction[];
    miscCorrection: MiscCorrection;
    dispatch: React.Dispatch<DcfRequestAction>;
    dcfRequestState: DcfRequestState;
    dcfRequest?: DcfRequest;
    onStageExistingDelete?: (attachmentId: string) => void;
    stagedDeleteIds?: string[];
    handleUploaderFilesUpdated?: any;
    handleStageDelete?: any;
    onFileAttachmentsUpdated?: (files: File[]) => void;
    disableEditFormFields?: boolean;
}

/**
 * Removes the selected properties for all or a selected correction record optionally specified by an index.
 * @param formApi is the Informed library formApi object.
 * @param propNames is a string array of CorrectionRecord properties to be removed.
 * @param recordIndex is an optional argument which specifes only a specific correction record for property removal.
 */
export const removeCorrectionRecordProperties = (
    formApi: any,
    propNames: string[],
    recordIndex?: number
) => {
    const CorrectionRecords = formApi.getValue("CorrectionRecords") as any[];

    if (CorrectionRecords && CorrectionRecords.length && CorrectionRecords.length > 0) {
        const removeProps = record =>
            record && propNames.forEach(propName => delete record[propName]);

        recordIndex
            ? removeProps(CorrectionRecords[recordIndex])
            : CorrectionRecords.forEach(removeProps);

        formApi.setState({ values: { ...formApi.getState().values, CorrectionRecords } });
    }
};

export const DataCorrectionRequestFormRecord = (props: DataCorrectionRequestFormRecordProps) => {
    const { dataItemConfigurationCollection } = useCurrentStudyConfigurationContext();
    const [initialValuesLoaded, setInitialValuesLoaded] = React.useState<boolean>();

    const formState = useFormState();
    const formApi = useFormApi();
    const client = useWebPortalApiClient();
    const { currentUserPermissions, currentUser } = useSecurityContext();

    React.useEffect(() => {
        const getVisits = async () => {
            if (props.selectedSubject) {
                props.dispatch(LoadingVisitOptionsAction.create(props.recordId));
                const visitResponse = await client.client.getVisits(`created,modified,visitScheduleItemId,id,subjectId,name,occurred`, `subjectId eq ${props.selectedSubject.id}`, "name");
                props.dispatch(LoadVisitOptionsAction.create(props.recordId, visitResponse));

                props.dispatch(LoadSessionOptionsAction.create(props.recordId, { value: [] }));
                props.dispatch(SelectSessionOptionAction.create(props.recordId, null, props.recordIndex, formApi));
            } else {
                props.dispatch(LoadVisitOptionsAction.create(props.recordId, { value: [] }));
                props.dispatch(SelectVisitOptionAction.create(props.recordId, null, props.recordIndex));
                formApi.setValue(`CorrectionRecords[${props.recordId}].VisitId`, "");

                props.dispatch(LoadSessionOptionsAction.create(props.recordId, { value: [] }));
                props.dispatch(SelectSessionOptionAction.create(props.recordId, null, props.recordIndex, formApi));
                formApi.setValue(`CorrectionRecords[${props.recordId}].SessionId`, "");
            }
        }

        getVisits();
    }, [props.selectedSubject]);

    React.useEffect(() => {
        const getSessions = async () => {
            if (props.selectedVisit && props.selectedSubject) {
                props.dispatch(LoadingSessionOptionsAction.create(props.recordId));
                const sessionResponse = await client.client.getSessions(undefined, `visitId eq ${props.selectedVisit?.id}`, "name");
                props.dispatch(LoadSessionOptionsAction.create(props.recordId, sessionResponse));
            } else {
                props.dispatch(LoadSessionOptionsAction.create(props.recordId, { value: [] }));
                props.dispatch(SelectSessionOptionAction.create(props.recordId, null, props.recordIndex, formApi));
                formApi.setValue(`CorrectionRecords[${props.recordId}].SessionId`, "");
            }
        }

        getSessions();
    }, [props.selectedVisit, props.selectedSubject]);

    React.useEffect(() => {
        const getInitialFormValues = async () => {
            if (initialValuesLoaded != true) {
                if (props.isEditFormRecord != undefined && props.isEditFormRecord == true) {
                    if (props.dcfRequest != undefined) {
                        formApi.setValue(`CorrectionRecords["${props.recordId}"].ChangeBackground`, props.dcfRequest?.additionalComments)
                        formApi.setValue(`CorrectionRecords["${props.recordId}"].SubjectId`, String(props.dcfRequest?.subjectId ?? ""))
                        props.onSubjectChange(String(props.dcfRequest?.subjectId ?? ""));

                        const visitResponse = await client.client.getVisits(`created,modified,visitScheduleItemId,id,subjectId,name,occurred`, `subjectId eq ${props.dcfRequest?.subjectId}`, "name");
                        props.dispatch(LoadVisitOptionsAction.create(props.recordId, visitResponse));
                        formApi.setValue(`CorrectionRecords["${props.recordId}"].VisitId`, String(props.dcfRequest?.visitId ?? ""))
                        props.onVisitChange(String(props.dcfRequest?.visitId ?? ""));

                        const sessionResponse = await client.client.getSessions(undefined, `visitId eq ${props.dcfRequest?.visitId}`, "name");
                        props.dispatch(LoadSessionOptionsAction.create(props.recordId, sessionResponse));
                        formApi.setValue(`CorrectionRecords["${props.recordId}"].SessionId`, String(props.dcfRequest?.sessionId ?? ""))
                        props.onSessionChange(String(props.dcfRequest?.sessionId ?? ""));
                        setInitialValuesLoaded(true);
                    }
                }
            }
        }
        getInitialFormValues();
    }, [props.dcfRequest?.additionalComments, props.recordId, props.selectedSubject?.id, props.selectedVisit?.id, props.dcfRequest?.visitId, props.dcfRequest?.sessionId]);

    React.useEffect(() => {
        if (!props.miscCorrection)
            return;
        let { CorrectionRecords = {} } = formApi.getFormState().values as any;
        if (!CorrectionRecords[props.recordId]) {
            CorrectionRecords = {
                ...CorrectionRecords,
                [props.recordId]: {
                    ChangeBackground: null,
                    SessionId: null,
                    SessionTypeId: null,
                    SubjectId: null,
                    VisitId: null,
                    CorrectionTypeId: null,
                },
            };
            formApi.setValues({ ...formApi.getFormState().values, CorrectionRecords });
        }
    }, [props.miscCorrection])


    const queryDataSelector = (recordState: DcfRecordState<any>): any[] => {
        if (!recordState || !recordState.query || !recordState.query.value) {
            return [];
        }

        return recordState.query.value;
    };

    const allOptionValue = "All";

    const [allVisitsSelected, setAllVisitsSelected] = React.useState(false);
    const [allSessionsSelected, setAllSessionsSelected] = React.useState(false);

    const hasCorrections = () => {
        return props.corrections && props.corrections.length > 0 || !!props.miscCorrection;
    };

    const subjectConfig = () => dataItemConfigurationCollection.find(dic => dic.configurableDataItemId == ConfigurableDataItemIds.SubjectId);


    const handleUploaderFilesUpdated = (files: File[]) => {
        props.onFileAttachmentChanged?.(files);
        props.onFileAttachmentsUpdated?.(files);
    };

    const handlePermissionToRemoveFileAttachments = () => {
        if (props.isEditFormRecord) {
            if (currentUserPermissions.some(x => x.Id === PermissionEnum.DeleteFile)) {

                return true;
            }
            else {
                return false;
            }
        } else {

            return true;
        }
    }

    return (
        <div data-recordid={props.recordId}>
            <div className="panel panel-default">
                <div className="panel-body">
                    <Scope scope={`CorrectionRecords["${props.recordId}"]`}>
                        <div className="row">
                            <div className="col-md-12">
                                <div className="form-group col-md-3">
                                    <label
                                        htmlFor={`select-subjectNumber-${props.recordId}`}
                                    >
                                        {subjectConfig().displayText}
                                    </label>
                                    &nbsp;
                                    {props.isLoadingSubjectOptions ? (
                                        <Loading
                                            className="ml-1"
                                            style={{ display: "inline-block" }}
                                            size={FontSize.OneX}
                                        />
                                    ) : null}
                                    <Dropdown
                                        name="SubjectId"
                                        disabled={props.isLoadingSubjectOptions || (hasCorrections()) || props.disableEditFormFields}
                                        id={`select-subjectNumber-${props.recordId}`}
                                        value={props.selectedSubject?.id ?? ""}
                                        field="SubjectId"
                                        className="form-control"
                                        onChange={e => {
                                            //Changing a subject resets the dropdowns
                                            const id = String((e as any).target.value || "");
                                            setAllVisitsSelected(false);
                                            setAllSessionsSelected(false);
                                            props.onSubjectChange(id);  
                                            formApi.setValue(`CorrectionRecords[${props.recordId}].SessionId`, "");
                                        }}
                                    >
                                        <option key={"OptionSelect"} value="" />
                                        {props.subjectOptions.map(s => (
                                            <option key={`Subject${s.id}`} value={s.id}>
                                                {s.number}
                                            </option>
                                        ))}
                                    </Dropdown>
                                </div>

                                <div className="form-group col-md-3">
                                    <label htmlFor="select-visit">Visit</label>&nbsp;
                                    {props.visits != null ? (
                                        props.visits.isLoading &&
                                            props.dcfRequestState.isLoadingVisits ? (
                                            <Loading
                                                className="ml-1"
                                                style={{ display: "inline-block" }}
                                                size={FontSize.OneX}
                                            />
                                        ) : null
                                    ) : null}
                                    <Dropdown
                                        name="VisitId"
                                        disabled={
                                            (props.dcfRequestState.isLoadingVisits &&
                                                props.visits &&
                                                props.visits.isLoading) || (hasCorrections()) || props.disableEditFormFields
                                        }
                                        id={`VisitId${props.recordId}`}
                                        field="VisitId"
                                        className="form-control"
                                        onChange={e => {
                                            if ((e as any).target.value === allOptionValue) {
                                                setAllVisitsSelected(true);
                                            } else {
                                                //De-selecting visits de-selects the session also.
                                                setAllVisitsSelected(false);
                                                setAllSessionsSelected(false);
                                            }
                                            formApi.setValue(`CorrectionRecords[${props.recordId}].SessionId`, "");
                                            props.onVisitChange((e as any).target.value);
                                        }}
                                    >
                                        <option key={"OptionSelect"} value="" />
                                        {queryDataSelector(props.visits).map(v => (
                                            <option key={`Visit${v.id}`} value={v.id}>
                                                {v.name}
                                            </option>
                                        ))}
                                    </Dropdown>
                                </div>

                                <div className="form-group col-md-3">
                                    <label htmlFor="select-session">Session</label>&nbsp;
                                    {props.sessions != null ? (
                                        props.dcfRequestState.isLoadingSessions ? (
                                            <Loading
                                                className="ml-1"
                                                style={{ display: "inline-block" }}
                                                size={FontSize.OneX}
                                            />
                                        ) : null
                                    ) : null}
                                    <Dropdown
                                        name="SessionId"
                                        disabled={
                                            props.dcfRequestState.isLoadingSessions || (hasCorrections()) || props.disableEditFormFields
                                        }
                                        id={`SessionId${props.recordId}`}
                                        field="SessionId"
                                        className="form-control"
                                        onChange={e => {
                                            setAllSessionsSelected((e as any).target.value === allOptionValue);
                                            props.onSessionChange((e as any).target.value);
                                        }}
                                    >
                                        <option key={"OptionSelect"} value="" />
                                        {queryDataSelector(props.sessions).map((s: Session) => (
                                            <option key={`Session${s.id}`} value={s.id}>
                                                {GetSessionLabel(s)}
                                            </option>
                                        ))}
                                    </Dropdown>
                                </div>
                                {!props.isEditFormRecord ? (
                                    <div className="form-group col-md-3 text-right">
                                        <button
                                            className="btn btn-danger btn-outline"
                                            type="button"
                                            disabled={props.isRemoveRecordButtonDisabled}
                                            onClick={() =>
                                                props.onRemoveClick(props.recordId)
                                            }
                                        >
                                            <FontAwesomeIcon icon={faXmark} /> Remove Record
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                        <div className="row">
                            <div className="form-group col-md-12">
                                <label htmlFor="changeBackground">Comments / Reason for Data Correction</label>
                                &nbsp;
                                <Tooltip id="changeBackgroundTooltip" class="tooltip" place="top" content={<span>Enter additional information that may help the change be processed promptly</span>} />
                                <i
                                    aria-hidden="true"
                                    className="fa fa-info-circle text-muted"
                                    data-tooltip-id="changeBackgroundTooltip"
                                />
                                <TextArea
                                    name="ChangeBackground"
                                    id={`changeBackground${props.recordId}`}
                                    field="ChangeBackground"
                                    className="form-control"
                                    validate={RequiredNotBlank}
                                    disabled={props.disableEditFormFields}
                                />
                            </div>
                        </div>
                        {!props.disableEditFormFields ?
                        <CorrectionTypeSelection
                            allSessionsSelected={allSessionsSelected}
                            allVisitsSelected={allVisitsSelected}
                            recordId={props.recordId}
                            corrections={props.dcfRequestState.corrections.get(props.recordId)}
                            miscCorrection={props.dcfRequestState.miscCorrection.get(props.recordId)}
                            subject={props.dcfRequestState.selectedSubjects.get(props.recordId)}
                            session={props.dcfRequestState.selectedSessions.get(props.recordId)}
                            visit={props.dcfRequestState.selectedVisits.get(props.recordId)}
                            dispatch={props.dispatch}
                            dcfRequest={props.dcfRequest}
                            isEditFormRecord={props.isEditFormRecord}
                            disableEditFormFields={props.disableEditFormFields}
                        /> : null}
                        <CorrectionPreviewPanel correctionType={props.corrections[0]?.correctionTypeId} corrections={props.corrections} miscCorrection={props.miscCorrection} />
                        <div className="row">
                            <FileAttachmentsUploader
                                onFileAttachmentsUpdated={handleUploaderFilesUpdated}
                                attachmentRelatesTo={AttachmentRelatesTo.DcfRequest}
                                requiresPermissions={[PermissionEnum.UploadDcfAttachments]}
                                formState={formState}
                                dcfRequest={props.dcfRequest}
                                onStageExistingDelete={props.onStageExistingDelete}
                                stagedDeleteIds={props.stagedDeleteIds}
                                permissionToRemoveFileAttachment={handlePermissionToRemoveFileAttachments()}
                                isDcfEditPage={props.isEditFormRecord}
                                disableEditFormFields={props.disableEditFormFields}
                            />
                        </div>
                    </Scope>
                </div>
            </div>
        </div>
    );
}
